/**
 * lib/attribution.js — first-touch + last-touch ad/UTM attribution capture.
 *
 * Runs on every page load:
 *  1. Parses `utm_*`, `gclid`, `fbclid` from the URL query string.
 *  2. Sets the FIRST-touch snapshot in localStorage (sticky until cleared).
 *  3. Sets the LAST-touch snapshot in sessionStorage (fresh per visit).
 *  4. Reads `_fbp` and `_fbc` cookies set by the Meta pixel.
 *  5. Generates a per-action `event_id` (UUID) for Pixel↔CAPI dedup.
 *
 * Exports:
 *   captureFromUrl()           — call once at app boot (idempotent)
 *   getAttribution()           — returns { utm_source, utm_medium, utm_campaign,
 *                                          utm_term, utm_content, gclid, fbclid,
 *                                          fbp, fbc, page_path }
 *   newEventId()               — fresh UUID for one conversion action
 *   firePixelLead(eventId)     — fires browser-side `fbq('track','Lead',…)` with
 *                                the supplied eventID so CAPI can dedup.
 *
 * Backed by sessionStorage so multi-page SPA navigation keeps the same
 * attribution context for a visit, while localStorage holds the first-
 * touch values for true cross-session attribution.
 */

const FIRST_KEY = "cr_attrib_first";
const LAST_KEY = "cr_attrib_last";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "team", "rep_id"];
const CLICK_IDS = ["gclid", "fbclid"];

function _readCookie(name) {
  if (typeof document === "undefined") return null;
  const re = new RegExp(`(?:^|;\\s*)${name}=([^;]+)`);
  const m = document.cookie.match(re);
  return m ? decodeURIComponent(m[1]) : null;
}

function _readUrlAttribution() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const out = {};
  [...UTM_KEYS, ...CLICK_IDS].forEach((k) => {
    const v = params.get(k);
    if (v) out[k] = v.slice(0, 200);
  });
  return out;
}

export function captureFromUrl() {
  if (typeof window === "undefined") return;
  const fresh = _readUrlAttribution();
  if (Object.keys(fresh).length === 0) return; // nothing in URL, leave caches alone

  // First-touch sticky — only set once per browser, never overwritten.
  try {
    if (!localStorage.getItem(FIRST_KEY)) {
      localStorage.setItem(FIRST_KEY, JSON.stringify({ ...fresh, captured_at: Date.now() }));
    }
  } catch { /* localStorage disabled */ }
  // Last-touch — always overwrite for this visit.
  try {
    sessionStorage.setItem(LAST_KEY, JSON.stringify({ ...fresh, captured_at: Date.now() }));
  } catch { /* sessionStorage disabled */ }
}

export function getAttribution() {
  if (typeof window === "undefined") return {};
  let last = {};
  let first = {};
  try { last = JSON.parse(sessionStorage.getItem(LAST_KEY) || "{}"); } catch { /* noop */ }
  try { first = JSON.parse(localStorage.getItem(FIRST_KEY) || "{}"); } catch { /* noop */ }
  // Prefer last-touch values; fall back to first-touch when last is empty
  // (e.g. visitor came back direct after an ad click two days ago).
  const merged = {};
  UTM_KEYS.forEach((k) => { merged[k] = last[k] || first[k] || ""; });
  CLICK_IDS.forEach((k) => { merged[k] = last[k] || first[k] || ""; });
  return {
    ...merged,
    fbp: _readCookie("_fbp") || "",
    fbc: _readCookie("_fbc") || "",
    page_path: typeof location !== "undefined" ? location.pathname : "/",
  };
}

export function newEventId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* noop */ }
  // Fallback for older browsers
  return `evt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

/** No-op — Meta Pixel was intentionally removed at the owner's request. */
export function firePixelLead() { /* noop */ }
